import { createSelector } from '@reduxjs/toolkit';

import { CalculatedLine, Component, isTruss } from '@paragon/contract-library';
import { Line2D, LineSegment2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { calculateLine } from '@paragon/relative-geometry';
import { calculateBearingGeometry, calculateMemberPolygon, getPlatePairPolygonVertices2D } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { SnapType } from '../../three/dimension/SnapVisual';
import { RootState } from '../index';
import { getActiveComponent } from './getActiveComponentSelector';
import { getVisibleReferenceLineGuids } from './getVisibleReferenceLineGuids';

export interface Snap {
  readonly position: Point2D;
  readonly snapType: SnapType;
}

export const getActiveComponentSnaps = createSelector(
  getActiveComponent,
  (state: RootState) => state.visualOptions.snapSettings,
  getVisibleReferenceLineGuids,

  (component, snapSettings, visibleReferenceLineGuids): readonly Snap[] => {
    if (!component) return [];

    const someSnapsVisible =
      snapSettings.showIntersectionSnaps ||
      snapSettings.showCentroidSnaps ||
      snapSettings.showVertexSnaps ||
      snapSettings.showMidLineSnaps;
    if (!someSnapsVisible) return [];

    const vertices = getComponentSnapVertices(component, visibleReferenceLineGuids);

    const snaps: Snap[] = [];

    if (snapSettings.showIntersectionSnaps) {
      const intersectionSnaps = getIntersectionSnaps(vertices);
      snaps.push(...intersectionSnaps);
    }

    if (snapSettings.showMidLineSnaps) {
      const midlineSnaps = getMidlineSnaps(vertices);
      snaps.push(...midlineSnaps);
    }

    if (snapSettings.showVertexSnaps) {
      const vertexSnaps = getVertexSnaps(vertices);
      snaps.push(...vertexSnaps);
    }

    if (snapSettings.showCentroidSnaps) {
      const centroidSnaps = getCentroidSnaps(vertices);
      snaps.push(...centroidSnaps);
    }

    return snaps;
  },
);

interface ComponentSnappingTargetData {
  readonly memberPolygons: Polygon2D[];
  readonly platePairPolygons: Polygon2D[];
  readonly bearingSegments: LineSegment2D[];
  readonly gussetVertices: Point2D[];
  readonly referenceLines: Line2D[];
}

class ComponentSnappingTargets implements ComponentSnappingTargetData {
  memberPolygons: Polygon2D[];
  platePairPolygons: Polygon2D[];
  bearingSegments: LineSegment2D[];
  gussetVertices: Point2D[];
  referenceLines: Line2D[];

  constructor(data: ComponentSnappingTargetData) {
    this.memberPolygons = data.memberPolygons;
    this.platePairPolygons = data.platePairPolygons;
    this.bearingSegments = data.bearingSegments;
    this.gussetVertices = data.gussetVertices;
    this.referenceLines = data.referenceLines;
  }

  get memberSegments(): LineSegment2D[] {
    return this.memberPolygons.flatMap((polygon) => polygon.edges);
  }

  get platePairSegments(): LineSegment2D[] {
    return this.platePairPolygons.flatMap((polygon) => polygon.edges);
  }

  get memberVertices(): Point2D[] {
    return this.memberPolygons.flatMap((polygon) => polygon.vertices);
  }

  get platePairVertices(): Point2D[] {
    return this.platePairPolygons.flatMap((polygon) => polygon.vertices);
  }

  get bearingVertices(): Point2D[] {
    return this.bearingSegments.flatMap((segment) => segment.endPoints);
  }
}

function getSnappableReferenceLines(
  component: Component,
  visibleReferenceLineGuids: ReadonlySet<string> | null,
): CalculatedLine[] {
  if (component.referenceLines == null) return []; // some components don't have relative geometry
  if (visibleReferenceLineGuids == null) return []; // will be null when reference lines are hidden

  return component.referenceLines.filter((line) => visibleReferenceLineGuids.has(line.guid));
}

function getComponentSnapVertices(
  component: Component,
  visibleReferenceLineGuids: ReadonlySet<string> | null,
): ComponentSnappingTargets {
  const members = isTruss(component) ? component.members.concat(component.braceMembers) : component.members;
  const memberPolygons = members.map((member) => calculateMemberPolygon(member, component)).filter(notNullish);

  const platePairPolygons = component.platePairs
    .filter((platePair) => platePair.platePlacement !== 'TopBottom') // we don't snap to top bottom plates
    .map(getPlatePairPolygonVertices2D)
    .map((platePairPolygons) => platePairPolygons.map(Polygon2D.fromVerticesIfValid))
    .flat()
    .filter(notNullish);

  const bearingSegments = component.bearings
    .map((bearing) => {
      const geometry = calculateBearingGeometry(bearing, component);
      if (!geometry) return null;

      const { normal, position, width } = geometry;
      const translation = width / 2;
      const segmentDirection = normal.rotateQuarterTurn();

      const point1 = position.translate(segmentDirection.toVector(translation));
      const point2 = position.translate(segmentDirection.toVector(-translation));

      return new LineSegment2D(point1, point2);
    })
    .filter(notNullish);

  const gussetVertices = component.gussets.flatMap((gusset) => gusset.geometry.map(Point2D.fromObject));

  const referenceLines = getSnappableReferenceLines(component, visibleReferenceLineGuids)
    .map((line) => calculateLine(line, component))
    .filter(notNullish);

  return new ComponentSnappingTargets({
    memberPolygons,
    platePairPolygons,
    bearingSegments,
    gussetVertices,
    referenceLines,
  });
}

function getIntersectionSnaps(targets: ComponentSnappingTargets): readonly Snap[] {
  const intersectionSnaps: Snap[] = [];

  for (const memberEdge of targets.memberSegments) {
    for (const plateEdge of targets.platePairSegments) {
      const intersection = memberEdge.intersectWithSegment(plateEdge);
      if (intersection) {
        intersectionSnaps.push({ position: intersection, snapType: 'Intersection' });
      }
    }

    for (const referenceLine of targets.referenceLines) {
      const intersection = memberEdge.intersectWithLine(referenceLine);
      if (intersection) {
        intersectionSnaps.push({ position: intersection, snapType: 'Intersection' });
      }
    }
  }

  for (const referenceLine of targets.referenceLines) {
    for (const otherReferenceLine of targets.referenceLines) {
      if (referenceLine === otherReferenceLine) continue;

      const intersection = referenceLine.intersectWithLine(otherReferenceLine);
      if (intersection) {
        intersectionSnaps.push({ position: intersection, snapType: 'Intersection' });
      }
    }
  }

  return intersectionSnaps;
}

function getMidlineSnaps(targets: ComponentSnappingTargets): readonly Snap[] {
  const midlineSnaps: Snap[] = [];

  const segments = [...targets.memberSegments, ...targets.platePairSegments, ...targets.bearingSegments];

  for (const segment of segments) {
    midlineSnaps.push({ position: segment.midpoint, snapType: 'Midline' });
  }

  return midlineSnaps;
}

function getVertexSnaps(targets: ComponentSnappingTargets): readonly Snap[] {
  const vertices = [
    ...targets.memberVertices,
    ...targets.gussetVertices,
    ...targets.platePairVertices,
    ...targets.bearingVertices,
  ];

  return vertices.map((point) => ({ position: point, snapType: 'Vertex' }));
}

function getCentroidSnaps(targets: ComponentSnappingTargets): readonly Snap[] {
  const polygons = [...targets.memberPolygons, ...targets.platePairPolygons];

  return polygons.map((polygon) => ({
    position: polygon.centroid,
    snapType: 'Centroid',
  }));
}
